/* RehabRenderer — AI After-Rehab Photo Renderer (Gemini Nano Banana).

   Modal component. Two invocation modes:
     • standalone: user drops a photo → picks tier/style → gets an after render
     • prefilled:  parent hands in a base64 photo + tier (e.g. from the AI
                   photo scan) → we auto-render on open

   Usage:
     <RehabRenderer
       open={open}
       onClose={() => setOpen(false)}
       initialPhoto={base64}         // optional
       initialTier="medium"          // optional
       address="123 Main St"         // optional (used on the social card)
     />
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Camera, Loader2, Sparkles, X, Download, RefreshCw, ArrowRight,
  MoveHorizontal, ImageIcon, ChevronDown, Send, Check, Home,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RehabRenderer({
  open, onClose,
  initialPhoto = null, initialTier = "medium",
  address = "", city = "", state = "", sqft = null, yearBuilt = null,
  leadId = null,
}) {
  const [meta, setMeta] = useState(null);
  const [quota, setQuota] = useState(null);
  const [beforeB64, setBeforeB64] = useState(initialPhoto);
  const [tier, setTier] = useState(initialTier);
  const [style, setStyle] = useState(null);        // null = auto-derive from tier
  const [roomHint, setRoomHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [render, setRender] = useState(null);      // {id, after_b64, mime_type, ...}
  const [sliderOpen, setSliderOpen] = useState(false);
  const [creditsAlert, setCreditsAlert] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/rehab-render/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/rehab-render/quota").then((r) => setQuota(r.data)).catch(() => {});
    // Reset when re-opened
    setBeforeB64(initialPhoto);
    setTier(initialTier);
    setStyle(null);
    setRender(null);
  }, [open, initialPhoto, initialTier]);

  const activeStyle = useMemo(() => {
    if (style) return style;
    const t = meta?.tiers?.find((x) => x.key === tier);
    return t?.default_style || "modern-farmhouse";
  }, [style, tier, meta]);

  const onPickFile = (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (JPG or PNG).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo too large — max 8 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const b64 = dataUrl.replace(/^data:[^;]+;base64,/, "");
      setBeforeB64(b64);
      setRender(null);
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!beforeB64) { toast.error("Add a 'before' photo first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rehab-render", {
        before_photo_b64: beforeB64,
        tier,
        style: activeStyle,
        room_hint: roomHint || null,
        address: address || null,
        city: city || null,
        state: state || null,
        sqft: sqft || null,
        year_built: yearBuilt || null,
        lead_id: leadId || null,
      }, { timeout: 120_000 });
      setRender(data);
      const q = await api.get("/rehab-render/quota");
      setQuota(q.data);
      toast.success("After-render ready");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Render failed — try a clearer photo.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const downloadSocialCard = async () => {
    if (!render?.id) return;
    try {
      const r = await api.get(`/rehab-render/${render.id}/social`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `before-after-${render.id.slice(0, 8)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Social card downloaded");
    } catch {
      toast.error("Couldn't build social card");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-3"
         data-testid="rehab-renderer-modal" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-[var(--line)] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-[var(--primary-dark)]" />
              <h2 className="font-display font-black text-xl">AI After-Rehab Renderer</h2>
            </div>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">
              Photo-realistic previews · powered by Gemini Nano Banana
            </div>
          </div>
          <div className="flex items-center gap-3">
            {quota && (
              <div className="text-xs text-[var(--ink-3)]" data-testid="rehab-render-quota">
                <b>{quota.remaining}</b>/{quota.cap} left today
              </div>
            )}
            <button className="btn-outline btn-icon-only" onClick={onClose} aria-label="Close"
                    data-testid="rehab-renderer-close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* STEP 1 · Photo */}
          <StepPhoto
            beforeB64={beforeB64}
            onPickFile={onPickFile}
            onClear={() => { setBeforeB64(null); setRender(null); }}
            fileRef={fileRef}
          />

          {/* STEP 2 · Tier + style */}
          {beforeB64 && (
            <StepConfig
              meta={meta}
              tier={tier} setTier={(t) => { setTier(t); setStyle(null); setRender(null); }}
              activeStyle={activeStyle} setStyle={(s) => { setStyle(s); setRender(null); }}
              roomHint={roomHint} setRoomHint={setRoomHint}
            />
          )}

          {/* Credits-low banner — the team has already been alerted, this
              just tells the user why nothing happened without scary red X's. */}
          {creditsAlert && (
            <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 flex items-start gap-3"
                 data-testid="rehab-renderer-credits-alert">
              <div className="w-6 h-6 flex-shrink-0 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm">i</div>
              <div className="text-sm">
                <div className="font-bold text-amber-900 mb-1">
                  AI render credits are temporarily unavailable
                </div>
                <div className="text-amber-800 text-[13px] leading-relaxed">
                  We&apos;ve already alerted the team — they&apos;ll top up the AI balance
                  shortly. Please try again in a few minutes. Your daily render quota
                  is untouched.
                </div>
              </div>
            </div>
          )}

          {/* Generate button */}
          {beforeB64 && !render && (
            <button
              onClick={generate}
              disabled={busy}
              className="btn-money w-full flex items-center justify-center gap-2"
              data-testid="rehab-renderer-generate"
            >
              {busy ? (
                <><Loader2 size={16} className="animate-spin" /> Rendering the &quot;after&quot;… ~15-30s</>
              ) : (
                <><Sparkles size={16} /> Render the After →</>
              )}
            </button>
          )}

          {/* Result */}
          {render && (
            <StepResult
              render={render}
              beforeB64={beforeB64}
              onSlider={() => setSliderOpen(true)}
              onDownloadCard={downloadSocialCard}
              onReset={() => { setRender(null); }}
            />
          )}
        </div>
        {sliderOpen && (
          <SliderCompareModal
            beforeB64={beforeB64}
            afterB64={render?.after_b64}
            afterMime={render?.mime_type}
            onClose={() => setSliderOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────────── STEP 1 · Photo picker ─────────────────── */
function StepPhoto({ beforeB64, onPickFile, onClear, fileRef }) {
  const [drag, setDrag] = useState(false);
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-bold mb-2">
        1. Before photo
      </div>
      {beforeB64 ? (
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)]"
             data-testid="rehab-renderer-before-preview">
          <img src={`data:image/jpeg;base64,${beforeB64}`}
               alt="Before" className="w-full max-h-72 object-cover" />
          <button
            onClick={onClear}
            className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 shadow"
            data-testid="rehab-renderer-clear-photo" aria-label="Remove photo"
          >
            <X size={14} />
          </button>
          <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
            Before
          </div>
        </div>
      ) : (
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); onPickFile(e.dataTransfer.files); }}
          className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
            drag ? "border-[var(--primary)] bg-[var(--primary)]/10"
                 : "border-[var(--line)] hover:border-[var(--primary)] hover:bg-slate-50"
          }`}
          data-testid="rehab-renderer-photo-dropzone"
        >
          <input ref={fileRef} type="file" accept="image/*"
                 className="hidden" onChange={(e) => onPickFile(e.target.files)}
                 data-testid="rehab-renderer-photo-input" />
          <ImageIcon size={32} className="mx-auto text-[var(--ink-3)] mb-2" />
          <div className="font-semibold text-sm text-[var(--ink)]">
            Drop a photo or click to browse
          </div>
          <div className="text-xs text-[var(--ink-3)] mt-1">
            JPG or PNG · up to 8 MB · works best on close-up interior/exterior shots
          </div>
        </label>
      )}
    </div>
  );
}

/* ─────────────────── STEP 2 · Tier + style + hint ─────────────────── */
function StepConfig({ meta, tier, setTier, activeStyle, setStyle,
                     roomHint, setRoomHint }) {
  const tiers = meta?.tiers || [];
  const styles = meta?.styles || [];
  const [styleOpen, setStyleOpen] = useState(false);
  const activeStyleLabel = styles.find((s) => s.key === activeStyle)?.label || activeStyle;

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-bold mb-2">
          2. Rehab tier
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {tiers.map((t) => (
            <button
              key={t.key}
              onClick={() => setTier(t.key)}
              className={`text-left rounded-lg border p-2.5 transition ${
                tier === t.key
                  ? "border-[var(--primary)] bg-[var(--primary)]/10"
                  : "border-[var(--line)] hover:border-[var(--primary)]"
              }`}
              data-testid={`rehab-renderer-tier-${t.key}`}
            >
              <div className="font-bold text-sm text-[var(--ink)]">{t.label}</div>
              <div className="text-[10px] text-[var(--ink-3)] mt-0.5 line-clamp-3 leading-snug">{t.scope.split(".")[0]}.</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-bold mb-2 flex items-center justify-between">
          <span>3. Style</span>
          <span className="text-[10px] text-[var(--ink-3)] normal-case font-normal tracking-normal">
            Auto-suggested for {tier}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setStyleOpen((v) => !v)}
          className="w-full input flex items-center justify-between"
          data-testid="rehab-renderer-style-picker"
        >
          <span className="font-semibold">{activeStyleLabel}</span>
          <ChevronDown size={16} className={`transition ${styleOpen ? "rotate-180" : ""}`} />
        </button>
        {styleOpen && (
          <div className="mt-2 border border-[var(--line)] rounded-lg divide-y max-h-56 overflow-y-auto"
               data-testid="rehab-renderer-style-list">
            {styles.map((s) => (
              <button
                key={s.key}
                onClick={() => { setStyle(s.key); setStyleOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition ${
                  activeStyle === s.key ? "bg-[var(--primary)]/10" : ""
                }`}
                data-testid={`rehab-renderer-style-${s.key}`}
              >
                <div className="font-semibold text-[var(--ink)]">{s.label}</div>
                <div className="text-[11px] text-[var(--ink-3)] mt-0.5 leading-snug line-clamp-2">
                  {s.description}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] font-bold mb-2">
          4. Room hint <span className="normal-case font-normal tracking-normal text-[10px]">(optional)</span>
        </div>
        <input
          type="text"
          value={roomHint}
          onChange={(e) => setRoomHint(e.target.value)}
          placeholder="e.g. kitchen, primary bath, front exterior, basement"
          className="input w-full"
          maxLength={80}
          data-testid="rehab-renderer-room-hint"
        />
      </div>
    </div>
  );
}

/* ─────────────────── STEP 3 · Result ─────────────────── */
function StepResult({ render, beforeB64, onSlider, onDownloadCard, onReset }) {
  return (
    <div className="border-t border-[var(--line)] pt-5" data-testid="rehab-renderer-result">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--primary-dark)] font-bold">
            Rendered
          </div>
          <div className="text-[11px] text-[var(--ink-3)]">
            {render.tier_label} rehab · {render.style_label}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onReset} className="btn-outline text-xs" data-testid="rehab-renderer-again">
            <RefreshCw size={12} className="inline mr-1" /> Change &amp; re-render
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={onSlider}
        className="w-full grid grid-cols-2 gap-2 rounded-xl overflow-hidden border border-[var(--line)] hover:border-[var(--primary)] transition group"
        data-testid="rehab-renderer-thumbs"
      >
        <div className="relative">
          <img src={`data:image/jpeg;base64,${beforeB64}`}
               alt="Before" className="w-full h-56 object-cover" />
          <div className="absolute top-2 left-2 bg-black/75 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
            Before
          </div>
        </div>
        <div className="relative">
          <img src={`data:${render.mime_type};base64,${render.after_b64}`}
               alt="After" className="w-full h-56 object-cover" />
          <div className="absolute top-2 left-2 bg-[var(--primary)] text-[var(--brand-primary-dark)] text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
            After
          </div>
        </div>
        <div className="col-span-2 bg-slate-50 px-3 py-2 text-xs text-[var(--ink-2)] flex items-center justify-center gap-1.5 group-hover:bg-[var(--primary)]/10 transition">
          <MoveHorizontal size={14} /> Click to open slider comparison
        </div>
      </button>

      {/* Save to Deal Room — the highest-leverage action for a wholesaler. */}
      <SaveToDealRoom renderId={render.id} />

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={onDownloadCard} className="btn-money flex-1 min-w-[180px]"
                data-testid="rehab-renderer-social-card">
          <Download size={14} className="inline mr-1" /> Download social card
        </button>
        <a
          href={`data:${render.mime_type};base64,${render.after_b64}`}
          download={`after-render-${render.id.slice(0, 8)}.png`}
          className="btn-outline"
          data-testid="rehab-renderer-download-after"
        >
          <Download size={14} className="inline mr-1" /> After only
        </a>
      </div>

      <div className="mt-3 text-[11px] text-[var(--ink-3)] italic border-t border-[var(--line)] pt-3">
        AI rendered — for marketing preview only. Actual finished work varies with contractor scope, materials, and site conditions.
      </div>
    </div>
  );
}

/* ─────────────────── Save-to-Deal-Room CTA ─────────────────── */
function SaveToDealRoom({ renderId }) {
  const [rooms, setRooms] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [includeCard, setIncludeCard] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState(null);   // {room_id, uploaded_count}

  const loadRooms = async () => {
    if (rooms) return;
    try {
      const r = await api.get("/rehab-render/deal-rooms/list");
      setRooms(r.data || []);
    } catch {
      toast.error("Couldn't load your deal rooms");
      setRooms([]);
    }
  };

  const doSave = async () => {
    if (!selectedId) { toast.error("Pick a Deal Room first"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/rehab-render/${renderId}/save-to-room`, {
        room_id: selectedId,
        include_social_card: includeCard,
      });
      setSavedTo({ room_id: selectedId, count: data.uploaded?.length || 0 });
      toast.success(
        `Saved ${data.uploaded?.length || 0} asset${data.uploaded?.length === 1 ? "" : "s"} to Deal Room`,
      );
      setPickerOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const selectedLabel = rooms?.find((r) => r.id === selectedId)?.label;

  if (savedTo) {
    return (
      <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3"
           data-testid="rehab-renderer-save-success">
        <div className="flex items-center gap-2 text-sm">
          <Check size={16} className="text-emerald-700" />
          <div className="font-semibold text-emerald-900">
            Saved to Deal Room — buyers will see the vision.
          </div>
        </div>
        <a
          href={`/app/deal-rooms/${savedTo.room_id}`}
          className="text-xs text-emerald-800 underline mt-1 inline-flex items-center gap-1"
          data-testid="rehab-renderer-open-deal-room"
        >
          Open Deal Room <ArrowRight size={11} />
        </a>
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid="rehab-renderer-save-to-room">
      {!pickerOpen ? (
        <button
          type="button"
          onClick={() => { setPickerOpen(true); loadRooms(); }}
          className="w-full bg-gradient-to-r from-[var(--brand-primary-dark)] to-[var(--brand-primary)] text-white font-bold rounded-lg px-4 py-3 flex items-center justify-center gap-2 hover:opacity-95 transition"
          data-testid="rehab-renderer-save-open"
        >
          <Send size={14} /> Save to Deal Room →
        </button>
      ) : (
        <div className="border border-[var(--primary)]/40 rounded-lg p-3 bg-white space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-[var(--primary-dark)] font-bold flex items-center gap-1.5">
              <Home size={11} /> Pick a Deal Room
            </div>
            <button onClick={() => setPickerOpen(false)} className="text-[var(--ink-3)] hover:text-[var(--ink)] p-1"
                    data-testid="rehab-renderer-save-cancel">
              <X size={14} />
            </button>
          </div>

          {rooms === null && (
            <div className="text-xs text-[var(--ink-3)] flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading rooms…
            </div>
          )}
          {rooms && rooms.length === 0 && (
            <div className="text-xs text-[var(--ink-3)]">
              You don&apos;t have any Deal Rooms yet.{" "}
              <a href="/v3/crm/deal-rooms" className="underline text-[var(--primary-dark)]">
                Create one →
              </a>
            </div>
          )}
          {rooms && rooms.length > 0 && (
            <div className="max-h-40 overflow-y-auto border border-[var(--line)] rounded divide-y"
                 data-testid="rehab-renderer-room-list">
              {rooms.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={`w-full text-left px-3 py-2 text-sm transition ${
                    selectedId === r.id
                      ? "bg-[var(--primary)]/15 border-l-2 border-[var(--primary)]"
                      : "hover:bg-slate-50"
                  }`}
                  data-testid={`rehab-renderer-room-${r.id}`}
                >
                  <div className="font-semibold text-[var(--ink)] truncate">{r.label}</div>
                  {r.status && (
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mt-0.5">
                      {r.status}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          <label className="flex items-center gap-2 text-xs cursor-pointer"
                 data-testid="rehab-renderer-include-card-row">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[var(--primary)]"
              checked={includeCard}
              onChange={(e) => setIncludeCard(e.target.checked)}
              data-testid="rehab-renderer-include-card"
            />
            <span className="text-[var(--ink-2)]">
              Also include the branded before/after social card
            </span>
          </label>

          <button
            onClick={doSave}
            disabled={!selectedId || saving}
            className="btn-money w-full disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="rehab-renderer-save-confirm"
          >
            {saving ? (
              <><Loader2 size={14} className="animate-spin" /> Saving…</>
            ) : (
              <>Save {includeCard ? "both" : "render"} to &quot;{selectedLabel || "..."}&quot; →</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Slider comparison modal ─────────────────── */
function SliderCompareModal({ beforeB64, afterB64, afterMime, onClose }) {
  const [pct, setPct] = useState(50);
  return (
    <div className="fixed inset-0 z-[110] bg-black/90 flex items-center justify-center p-4"
         onClick={onClose} data-testid="rehab-renderer-slider-modal">
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-white font-display font-black text-lg">Before / After · drag to compare</div>
          <button onClick={onClose} className="text-white p-1 hover:text-[var(--primary)]"
                  data-testid="rehab-renderer-slider-close">
            <X size={22} />
          </button>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-white/20 select-none">
          <img src={`data:image/jpeg;base64,${beforeB64}`}
               alt="Before" className="block w-full" />
          <div className="absolute inset-0 overflow-hidden" style={{ width: `${pct}%` }}>
            <img src={`data:${afterMime};base64,${afterB64}`}
                 alt="After" className="block w-full h-full object-cover"
                 style={{ minWidth: "100%" }} />
          </div>
          <div
            className="absolute inset-y-0 w-1 bg-[var(--primary)]"
            style={{ left: `calc(${pct}% - 2px)` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 bg-white rounded-full shadow-lg p-2"
            style={{ left: `calc(${pct}% - 16px)` }}
          >
            <MoveHorizontal size={16} className="text-[var(--brand-primary-dark)]" />
          </div>
          <div className="absolute top-3 left-3 bg-black/70 text-white text-xs font-bold uppercase tracking-widest px-2 py-1 rounded">
            Before
          </div>
          <div className="absolute top-3 right-3 bg-[var(--primary)] text-[var(--brand-primary-dark)] text-xs font-bold uppercase tracking-widest px-2 py-1 rounded">
            After
          </div>
        </div>
        <input
          type="range" min={0} max={100} value={pct}
          onChange={(e) => setPct(Number(e.target.value))}
          className="w-full mt-4 accent-[var(--primary)]"
          aria-label="Before/After slider"
          data-testid="rehab-renderer-slider-input"
        />
      </div>
    </div>
  );
}
